import { Link } from 'expo-router';
import { CheckIcon, ScanLineIcon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';

import { ReportContentSheet } from '@/components/moderation/report-content-sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@sixam/ui/avatar';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import {
  fetchEventAttendees,
  partitionAttendeesByCheckIn,
  type EventAttendee,
} from '@/lib/event-attendance';
import { BRAND } from '@sixam/config/brand';

type EventAttendeesProps = Readonly<{
  /** When true, show a "Check member in" bar that scans runners into this event. */
  enableCheckIn?: boolean;
  eventId: string;
  flat?: boolean;
  /** Reports the roster size after each load, so a parent count can stay in sync. */
  onCountChange?: (count: number) => void;
  /** Changing this refetches the roster — bump it after a check-in. */
  refreshKey?: number;
}>;

type EventAttendeesSummaryProps = Readonly<{
  attendees: EventAttendee[];
  error?: string | null;
  isLoading?: boolean;
  flat?: boolean;
  onReport?: (attendee: EventAttendee) => void;
}>;

function displayAttendeeName(attendee: EventAttendee): string {
  return attendee.fullName ?? '6am member';
}

function AttendeeChip({
  attendee,
  onReport,
}: Readonly<{ attendee: EventAttendee; onReport?: (attendee: EventAttendee) => void }>) {
  const name = displayAttendeeName(attendee);
  const initials = name.slice(0, 2).toUpperCase();
  return (
    <Pressable
      accessibilityHint="Report or block this member"
      accessibilityLabel={name}
      accessibilityRole="button"
      className="w-20 items-center gap-1.5 active:opacity-70"
      disabled={!onReport}
      onPress={() => onReport?.(attendee)}
    >
      <View>
        <Avatar
          alt={`${name}'s avatar`}
          className={`size-12 ${attendee.isCheckedIn ? '' : 'opacity-60'}`}
        >
          {attendee.avatarUrl ? <AvatarImage source={{ uri: attendee.avatarUrl }} /> : null}
          <AvatarFallback>
            <Text variant="label">{initials}</Text>
          </AvatarFallback>
        </Avatar>
        {attendee.isCheckedIn ? (
          <View
            className="absolute -bottom-0.5 -right-0.5 size-5 items-center justify-center rounded-full border-2 border-background"
            style={{ backgroundColor: BRAND.color.orange }}
          >
            <Icon as={CheckIcon} className="size-3 text-white" />
          </View>
        ) : null}
      </View>
      <Text className="text-center text-xs" numberOfLines={2}>
        {name}
      </Text>
    </Pressable>
  );
}

function AttendeeGroup({
  attendees,
  onReport,
  title,
}: Readonly<{
  attendees: EventAttendee[];
  onReport?: (attendee: EventAttendee) => void;
  title: string;
}>) {
  if (attendees.length === 0) {
    return null;
  }
  return (
    <View className="gap-2">
      <Text variant="label" className="text-muted-foreground">
        {title}
      </Text>
      <View className="flex-row flex-wrap gap-3">
        {attendees.map((attendee) => (
          <AttendeeChip attendee={attendee} key={attendee.id} onReport={onReport} />
        ))}
      </View>
    </View>
  );
}

export function EventAttendeesSummary({
  attendees,
  error,
  flat = true,
  isLoading,
  onReport,
}: EventAttendeesSummaryProps) {
  const { checkedIn, missing } = partitionAttendeesByCheckIn(attendees);

  return (
    <View className={flat ? 'gap-4' : 'gap-4 rounded-md border-2 border-border bg-card p-4'}>
      <View className="flex-row items-center justify-between gap-3">
        <View className="min-w-0 flex-1">
          <Text variant="large">Attendees</Text>
          <Text variant="muted">
            {isLoading
              ? 'Loading runners...'
              : `${checkedIn.length} of ${attendees.length} checked in`}
          </Text>
        </View>
        {isLoading ? <ActivityIndicator /> : null}
      </View>

      {error ? (
        <Text className="text-sm text-destructive" selectable>
          {error}
        </Text>
      ) : null}

      {!isLoading && !error && attendees.length === 0 ? (
        <Text variant="muted">No registered runners yet.</Text>
      ) : null}

      <AttendeeGroup
        attendees={checkedIn}
        onReport={onReport}
        title={`Checked in (${checkedIn.length})`}
      />
      <AttendeeGroup
        attendees={missing}
        onReport={onReport}
        title={`Missing (${missing.length})`}
      />
    </View>
  );
}

export function EventAttendees({
  enableCheckIn = false,
  eventId,
  flat = true,
  onCountChange,
  refreshKey,
}: EventAttendeesProps) {
  const [attendees, setAttendees] = React.useState<EventAttendee[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  /*
    Reporting and blocking must never sit behind a release flag: App Review has
    to be able to reach them. The member profile screen is gated by
    `memberProfiles`, so the roster carries its own copy — tapping any runner
    here opens the same sheet, on a surface that is always available.
  */
  const [reported, setReported] = React.useState<EventAttendee | null>(null);

  React.useEffect(() => {
    let active = true;

    async function load() {
      try {
        const data = await fetchEventAttendees(eventId);
        if (active) {
          setAttendees(data);
          setError(null);
          onCountChange?.(data.length);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load attendees.');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      active = false;
    };
  }, [eventId, onCountChange, refreshKey]);

  return (
    <View className="gap-3">
      {enableCheckIn ? (
        <Link asChild href={{ params: { id: eventId }, pathname: '/admin/check-in/[id]' }}>
          <Button>
            <ScanLineIcon color="white" size={18} />
            <Text>Check member in</Text>
          </Button>
        </Link>
      ) : null}

      <EventAttendeesSummary
        attendees={attendees}
        error={error}
        flat={flat}
        isLoading={isLoading}
        onReport={setReported}
      />

      <ReportContentSheet
        contentLabel={reported ? displayAttendeeName(reported) : 'this member'}
        isPresented={reported !== null}
        onBlocked={() =>
          setAttendees((current) => current.filter((row) => row.id !== reported?.id))
        }
        onDismiss={() => setReported(null)}
        subjectId={reported?.id ?? ''}
        subjectType="profile"
        subjectUserId={reported?.id ?? null}
      />
    </View>
  );
}
